import { isSSR } from '@helpers/ssr';
import React, { useEffect, useState } from 'react';

import { getWinePageInformation } from '@lib/api/wine';

interface WinePageContextProps {
  isLoading?: boolean;
  pageInformation: any;
  updatePageInformation?: (pageInformation: any) => void;
  updateVintage?: ({ year, priceId }: { year: string; priceId: string }) => void;
}

interface WinePageProviderProps {
  pageInformation: any;
  children: React.ReactNode;
}
export const WinePageContext = React.createContext<WinePageContextProps>({
  pageInformation: {},
});

export const WinePageProvider = ({ pageInformation, children }: WinePageProviderProps) => {
  const [pageInformationState, setPageInformationState] = useState(pageInformation);
  const [isLoading, setIsLoading] = useState(true);

  const availability = pageInformation?.prices_and_availability?.availability;
  const vintage = pageInformation?.vintage || availability?.vintage;
  const hashParams = !isSSR
    ? new URLSearchParams(globalThis.location?.hash?.substring(1))
    : new Map();

  const year = hashParams.get('year');
  const priceId = hashParams.get('price_id');

  const [urlhashParams, setUrlHashParams] = useState({
    year,
    priceId,
  });

  const updateHashParams = () => {
    if (!year && !priceId) {
      return;
    }
    const newUrlHashParams = {
      year,
      priceId,
    };

    setUrlHashParams(newUrlHashParams);
  };

  useEffect(() => {
    const handlePopstate = () => {
      updateHashParams();
    };

    globalThis.addEventListener('hashchange', handlePopstate);

    updateHashParams();

    return () => {
      globalThis.removeEventListener('hashchange', handlePopstate);
    };
  }, []);

  useEffect(() => {
    if (urlhashParams?.year) {
      getWinePageInformation({
        wineId: vintage.wine.id,
        year: urlhashParams.year,
        priceId: urlhashParams.priceId,
      })
        .then(({ page_information }) => {
          const newState = {
            ...page_information,
            prices_and_availabilities: pageInformation?.prices_and_availabilities,
          };
          setPageInformationState(newState);
          setIsLoading(false);
        })
        .catch(() => {})
        .finally(() => {
          setIsLoading(false);
        });
    }

    document.getElementById('navigation-container')?.scrollIntoView({ behavior: 'smooth' });
  }, [urlhashParams?.year, urlhashParams?.priceId]);

  function updateVintage({ year, priceId }) {
    const hashParams = new URLSearchParams(globalThis.location?.hash?.substring(1));
    if (year) {
      hashParams.set('year', year);
    } else {
      hashParams.delete('year');
    }
    if (priceId) {
      hashParams.set('price_id', priceId);
    } else {
      hashParams.delete('price_id');
    }
    globalThis.location.hash = hashParams.toString();
  }

  const values = {
    isLoading,
    updateVintage,
    pageInformation: pageInformationState,
    updatePageInformation: setPageInformationState,
  };

  return <WinePageContext.Provider value={values}>{children}</WinePageContext.Provider>;
};
